import React, { useEffect, useMemo, useState, useRef } from "react";
import { MapContainer, TileLayer, Tooltip, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./HealthCenters.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";

const codeTypeMap = {
    1:  { code: 'CHC',      name: 'Community Health Center', color: '#0000FF' },
    2:  { code: 'CHCI',     name: 'Community Health Center (Internment)', color: '#00FF00' },
    3:  { code: 'HP',       name: 'Health Post', color: '#da17b0ff' },
    4:  { code: 'RH',       name: 'Referral Hospital', color: '#390b13ff' },
    5:  { code: 'RGH',      name: 'Regional Hospital', color: '#FFD700' },
    6:  { code: 'NH',       name: 'National Hospital', color: '#FF4500' },
    7:  { code: 'CL',       name: 'Clinic', color: '#FFA500' },
    8:  { code: 'CL & PHA', name: 'Clinic and Pharmacy', color: '#008080' },
    9:  { code: 'PHA',      name: 'Pharmacy', color: '#800080' },
    10: { code: 'DRHC',     name: 'Disability Rehabilitation Center', color: '#6B8E23' },
    11: { code: 'MDRHC',    name: 'Multi Drug Rehabilitation Center', color: '#20B2AA' },
    12: { code: 'MRHC',     name: 'Mental Rehabilitation Center', color: '#9932CC' },
};
const defaultColor = '#757575';

// Helper to get color and name for code_type
const getTypeInfo = (codeType) => codeTypeMap[String(codeType)] || { name: 'Unknown', color: defaultColor };

const INITIAL_VIEW_STATE = {
    longitude: 125.7275,
    latitude: -8.8742,
    zoom: 8,
};

const HealthCenters = () => {
    const [hospitalData, setHospitalData] = useState([]);
    const [selectedData, setSelectedData] = useState(null);
    const [selectedCoords, setSelectedCoords] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [codeFilter, setCodeFilter] = useState("All");
    const [showFullServices, setShowFullServices] = useState(false);
    // Add a loading state
    const [loading, setLoading] = useState(true);
    // Add this state
    const [showTagsPanel, setShowTagsPanel] = useState(false);
    
    // Use useRef instead of useState for map instance
    const mapRef = useRef(null);

    useEffect(() => {
        setLoading(true);
        fetch(`${process.env.REACT_APP_BACKEND_API_URL_LOCAL}roadaccident/get_hospital_details/`)
            .then((response) => response.json())
            .then((data) => {
                setHospitalData(data);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error loading hospital data:", error);
                setLoading(false);
            });
    }, []);

   

    // Filter hospitals by code_type and search
    const hospitals = useMemo(() => {
        if (!hospitalData || !Array.isArray(hospitalData)) return [];
        return hospitalData
            .filter((item) => item.latitude && item.longitude)
            .filter((item) => codeFilter === "All" || String(item.code_type) === String(codeFilter))
            .filter((item) => {
                const q = searchTerm.trim().toLowerCase();
                if (!q) return true;
                return (
                    `${item.name || ""} ${item.municipality || ""} ${item.facility_type || ""}`.toLowerCase().includes(q)
                );
            })
            .map((item) => {
                const coords = [parseFloat(item.latitude), parseFloat(item.longitude)];
                return { coords, props: item };
            });
    }, [hospitalData, codeFilter, searchTerm]);

    const totalVisible = hospitals.length;

    // Enhanced map focus function with better animation and zoom control
    const focusOnPoint = (coords, zoomLevel = 15, animate = true) => {
        console.log('focusOnPoint called with:', coords, zoomLevel, animate);
        console.log('mapRef.current:', mapRef.current);
        
        if (mapRef.current && coords) {
            const currentZoom = mapRef.current.getZoom();
            const targetZoom = Math.max(zoomLevel, currentZoom);
            
            console.log('Focusing map to:', coords, 'with zoom:', targetZoom);
            
            if (animate) {
                mapRef.current.flyTo(coords, targetZoom, {
                    duration: 1.5,
                    easeLinearity: 0.25
                });
            } else {
                mapRef.current.setView(coords, targetZoom);
            }
        } else {
            console.log('Map instance or coordinates not available');
        }
    };

    // Focus on first result when filtering or searching
    const focusOnFirstResult = () => {
        console.log('focusOnFirstResult called, hospitals count:', hospitals.length);
        if (hospitals.length > 0 && mapRef.current) {
            const firstHospital = hospitals[0];
            console.log('Focusing on first hospital:', firstHospital);
            focusOnPoint(firstHospital.coords, 10, true);
        }
    };

    // Enhanced handleSelectItem with better focus
    const handleSelectItem = (item) => {
        console.log('handleSelectItem called with:', item);
        setSelectedData(item.props);
        setSelectedCoords(item.coords);
        focusOnPoint(item.coords, 15, true);
    };

    // Enhanced search handler with focus on results
    const handleSearchChange = (e) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        
        // Focus on first result after search if there are results
        setTimeout(() => {
            if (hospitals.length > 0) {
                focusOnFirstResult();
            }
        }, 300);
    };

    // Enhanced filter handler with focus on results
    const handleFilterChange = (filterKey) => {
        console.log('handleFilterChange called with:', filterKey);
        setCodeFilter(filterKey);
        setSelectedData(null);
        setSelectedCoords(null);
        
        // Zoom out to show the whole country when filters are applied
        if (mapRef.current) {
            console.log('Zooming out to country view');
            mapRef.current.flyTo(
                [INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude], 
                INITIAL_VIEW_STATE.zoom, 
                {
                    duration: 1.5,
                    easeLinearity: 0.25
                }
            );
        }
    };

    const toTitleCase = (value) => {
        if (!value || typeof value !== "string") return value;
        return value
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    // Cluster color logic
    const getClusterColor = (cluster) => {
        if (codeFilter !== "All") {
            // Always use the selected tag's color for clusters
            const selectedTypeInfo = codeTypeMap[String(codeFilter)];
            return selectedTypeInfo ? selectedTypeInfo.color : defaultColor;
        }
        // For "All", use the most common color in the cluster
        const markers = cluster.getAllChildMarkers();
        const colorCounts = {};
        markers.forEach(marker => {
            const color = marker.options.pathOptions?.color || defaultColor;
            colorCounts[color] = (colorCounts[color] || 0) + 1;
        });
        return Object.entries(colorCounts).sort((a, b) => b[1] - a[1])[0][0];
    };

    const iconCreateFunction = (cluster) => {
        const count = cluster.getChildCount();
        const color = getClusterColor(cluster);
        return L.divIcon({
            html: `<div style="
                background:${color};
                color:#fff;
                border-radius:50%;
                width:32px;
                height:32px;
                display:flex;
                align-items:center;
                justify-content:center;
                font-weight:bold;
                font-size:16px;
                border:2px solid #fff;
            ">${count}</div>`,
            className: "custom-cluster-icon",
            iconSize: [32, 32]
        });
    };


    // Prepare tags: All + codeTypeMap + Other
    const tagList = [
        { key: "All", name: "All", color: "#757575" },
        ...Object.entries(codeTypeMap).map(([key, info]) => ({
            key,
            name: info.name,
            color: info.color
        })),
        // { key: "Other", name: "Other", color: "#bdbdbd" }
    ];

    // Only show these fields in details, with custom labels
    const detailsFields = [
        { key: "name", label: "Name" },
        { key: "facility_type", label: "Facility Type" },
        { key: "elevation", label: "Elevation" },
        { key: "property_type", label: "Property Type" },
        { key: "operating_days", label: "Operating Days" },
        { key: "operating_hours", label: "Operating Hours" },
        { key: "total_beds", label: "Total Beds" },
        { key: "maternity_beds", label: "Maternity Beds" },
        { key: "location", label: "Location" },
        { key: "municipality", label: "Municipality" },
        { key: "adm2", label: "Post Administrative" },
        { key: "suco", label: "Suco" },
        { key: "services", label: "Services" },
    ];

    return (
        <div className="hc-wrapper">
            <div className={`hc-panel open`}>
                <div className="hc-panel-header">
                    <div className="hc-title">Health Centers</div>
                    <span className="hc-count-badge" title="Visible facilities count">{totalVisible}</span>
                </div>
                <div className="hc-panel-content">
                    <div className="hc-controls">
                        <input
                            className="hc-search"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search by name, municipality, or type..."
                        />
                    </div>
                    <div className="hc-list" role="list">
                        {loading ? (
                            <div className="hc-empty">Loading health centers...</div>
                        ) : (
                            hospitals.map((item, idx) => {
                                return (
                                    <button key={`h-${idx}`} className="hc-list-item" onClick={() => handleSelectItem(item)}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {/* Colored dot for code_type */}
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "50%",
                                                    background: codeTypeMap[item.props.code_type]?.color || defaultColor,
                                                    border: "1px solid #ccc"
                                                }}
                                            ></span>
                                            <div className="hc-list-title">{item.props.name || "Unnamed Facility"}</div>
                                        </div>
                                        <div className="hc-list-sub">{toTitleCase(item.props.suco) || "Unknown Suco"}, {toTitleCase(item.props.adm2) || "Unknown Post Adminstrative"},{toTitleCase(item.props.municipality) || "Unknown Municipality"} </div>
                                        <div className="hc-list-code">{item.props.facility_type}</div>
                                    </button>
                                );
                            })
                        )}
                        {totalVisible === 0 && !loading && <div className="hc-empty">No facilities match your filters.</div>}
                    </div>
                </div>
            </div>
            <MapContainer
                center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
                zoom={INITIAL_VIEW_STATE.zoom}
                className="mapcontainerall"
                ref={mapRef}
                attributionControl={false}
            >
              <MapHomeControl />
              <MapInfoControl text="Locations of hospitals and health centers relevant for earthquake emergency response." />
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution=""
                />
                {/* Add key={codeFilter} to force re-render */}
                <MarkerClusterGroup iconCreateFunction={iconCreateFunction} key={codeFilter}>
                    {hospitals.map((item, idx) => {
                        const typeInfo = getTypeInfo(item.props.code_type);
                        return (
                            <CircleMarker
                                key={`mh-${idx}`}
                                center={item.coords}
                                radius={6}
                                pathOptions={{
                                    color: typeInfo.color,
                                    fillColor: typeInfo.color,
                                    fillOpacity: 0.8,
                                    weight: 2
                                }}
                                eventHandlers={{ click: () => handleSelectItem(item) }}
                            >
                                <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent={false} sticky>
                                    {item.props.name || "Facility"}{" "}
                                    {typeInfo.name ? `(${typeInfo.name})` : ""}
                                </Tooltip>
                            </CircleMarker>
                        );
                    })}
                </MarkerClusterGroup>
                {selectedCoords && (
                    <CircleMarker center={selectedCoords} radius={14} pathOptions={{ color: "#1e88e5", weight: 2, fillOpacity: 0.15 }} />
                )}
            </MapContainer>
            {/* Bottom tags panel */}
            {showTagsPanel && (
                <div className="hc-tags-layer-panel">
                    <div className="hc-tags-panel-header">
                        <span className="hc-tags-panel-title">Facility Types</span>
                        <button className="hc-tags-panel-close" onClick={() => setShowTagsPanel(false)}>✕</button>
                    </div>
                    <div className="hc-tags-panel-body">
                        {tagList.map(tag => (
                            <button
                                key={tag.key}
                                className={`hc-tag${String(codeFilter) === String(tag.key) ? " active" : ""}`}
                                style={{
                                    background: String(codeFilter) === String(tag.key) ? tag.color : "#fff",
                                    color: String(codeFilter) === String(tag.key) ? "#fff" : "#1976d2",
                                    border: `1px solid ${tag.color}`,
                                    marginBottom: 8,
                                    borderRadius: 16,
                                    padding: "4px 12px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    width: "100%"
                                }}
                                onClick={() => handleFilterChange(tag.key)}
                            >
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        background: tag.color,
                                        border: "1px solid #ccc"
                                    }}
                                ></span>
                                {tag.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {/* Filter toggle button */}
            <button
                className="hc-tags-toggle-btn"
                onClick={() => setShowTagsPanel(true)}
                title="Show/Hide Facility Type Filters"
            >
                <span role="img" aria-label="layers">🗂️</span>
            </button>
            {/* Details panel */}
            {selectedData && (
                <div className="hc-details">
                    <div className="hc-details-header">
                        <div className="hc-details-title">Details</div>
                        <button className="hc-close" onClick={() => { setSelectedData(null); setSelectedCoords(null); setShowFullServices(false); }}>✕</button>
                    </div>
                    <div className="hc-details-body">
                        {detailsFields.map(({ key, label }) => {
                            const value = selectedData[key];
                            if (key === "services") {
                                if (!value || String(value).trim() === "") return null;
                                const words = String(value).trim().split(/\s+/);
                                if (showFullServices) {
                                    return (
                                        <div className="hc-detail-row" key={key}>
                                            <div className="hc-detail-key">{label}</div>
                                            <div className="hc-detail-value">{value}</div>
                                            <button type="button" className="hc-read-less" onClick={() => setShowFullServices(false)}>Read less</button>
                                        </div>
                                    );
                                }
                                const shortText = words.length > 50 ? words.slice(0, 50).join(" ") + "..." : value;
                                return (
                                    <div className="hc-detail-row" key={key}>
                                        <div className="hc-detail-key">{label}</div>
                                        <div className="hc-detail-value">
                                            {shortText}
                                            {words.length > 50 && (
                                                <button type="button" className="hc-read-more" onClick={() => setShowFullServices(true)}>Read more</button>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                            if (value !== undefined && value !== null && String(value).trim() !== "") {
                                return (
                                    <div className="hc-detail-row" key={key}>
                                        <div className="hc-detail-key">{label}</div>
                                        <div className="hc-detail-value">{String(value)}</div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HealthCenters;